import type { Remaining } from './useCountdown';

const pad = (n: number) => String(n).padStart(2, '0');

/** One flip-card digit pair; the key change restarts the flip animation on every new value. */
export function FlipTile({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-full overflow-hidden rounded-card bg-brand text-white shadow-overlay [perspective:400px]">
        <span
          key={value}
          className="block origin-top animate-flip py-4 font-heading text-3xl font-bold tabular-nums motion-reduce:animate-none sm:py-6 sm:text-5xl"
        >
          {pad(value)}
        </span>
        <span className="absolute inset-x-0 top-1/2 h-px bg-brand-800/60" />
      </div>
      <span className="text-xs font-medium tracking-wide text-slate uppercase">{label}</span>
    </div>
  );
}

/** Days · Hours · Minutes · Seconds. Hidden from screen readers; pages announce a summary instead. */
export function CountdownTiles({ remaining }: { remaining: Remaining }) {
  return (
    <div
      className="grid w-full max-w-md grid-cols-4 gap-2 sm:gap-4"
      aria-hidden
      data-testid="tiles"
    >
      <FlipTile value={remaining.days} label="Days" />
      <FlipTile value={remaining.hours} label="Hours" />
      <FlipTile value={remaining.minutes} label="Minutes" />
      <FlipTile value={remaining.seconds} label="Seconds" />
    </div>
  );
}

const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "6 days, 4 hours and 12 minutes": minute precision, so the live region speaks once a minute. */
export function describeRemaining({ days, hours, minutes }: Remaining): string {
  const parts = [
    days ? unit(days, 'day') : '',
    hours ? unit(hours, 'hour') : '',
    unit(minutes, 'minute'),
  ].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts[0]!;
}
